/**
 * A comemoração de UMA MARCA: metade e meta da meta total dela.
 *
 * Pedido em 14/10. É uma família à parte da de `marcos.ts`, que olha o TOTAL
 * GERAL: aqui o gatilho é a meta da divisão (`divisao.meta`), e cada marca tem
 * as suas duas festas — a dos 50 % e a dos 100 %.
 *
 * Vive fora do `.tsx` pela mesma razão do `marcos.ts` e do `ranking.ts`: o
 * `node --test` remove tipos de `.ts` mas não compila JSX, então o que é
 * testável mora aqui e o componente só desenha.
 *
 * As quatro regras que o telão impôs, e que estão todas aqui:
 *
 *  1. **A primeira leitura não comemora** — ela ancora. Sem isso, abrir o telão
 *     com três marcas já acima de 50 % joga três festas em fila no primeiro
 *     segundo, por marcos batidos antes de alguém ligar a tela. É o mesmo que o
 *     `usarComemoracao` faz com os milhões do total.
 *  2. **A meta vence a metade** quando a marca cruza as duas no mesmo ciclo.
 *     Um pedido grande entrando numa marca pequena faz isso, e enfileirar 32 s
 *     de festa para a mesma marca deixaria o telão comemorando passado.
 *  3. **Nada se descarta entre marcas.** Duas marcas que batem no mesmo ciclo
 *     entram em FILA, da maior venda para a menor: cada festa acontece uma vez
 *     no evento, e descartar significa que aquela marca nunca teve a dela.
 *  4. **O que já foi celebrado fica em `localStorage`.** O telão passa dias
 *     ligado e a página recarrega (deploy, F5, reconexão do SSE).
 */

import type { Divisao } from './api'

/**
 * Quanto tempo cada festa de marca fica na tela.
 *
 * Nasceram em 12 s e 20 s; a metade subiu para 20 s no mesmo dia, ao ver a tela
 * pronta: são 15 linhas de ranking, e doze segundos não dão para ler o nome do
 * 15º de 4 metros. Duas marcas cruzando no mesmo ciclo passam a segurar a
 * rotação por 40 s somados — é o preço, e ele foi aceito.
 */
export const DURACAO_METADE_MARCA_MS = 20_000
export const DURACAO_META_MARCA_MS = 20_000

/** Quantas peças de confete cada uma joga, e de que cor. */
export const CONFETES_METADE_MARCA = 80
export const CONFETES_META_MARCA = 160

/** Os dois estágios que rendem comemoração. */
export type TipoDeFesta = 'metade' | 'meta'

/** Uma festa de marca pendente ou em cartaz. */
export type FestaDeMarca = {
  /** `id` da divisão. */
  marca: string
  tipo: TipoDeFesta
  /** O vendido no instante em que ela foi detectada — é o número da tela. */
  vendido: number
}

/**
 * A chave da memória, uma por marca e tipo: `warroom.festaMarca.lez.meta`.
 *
 * Por marca e não uma lista serializada porque gravação concorrente de duas
 * marcas no mesmo ciclo não pode perder uma das duas — e `localStorage` não tem
 * leitura-escrita atômica.
 */
export function chaveDaFesta(marca: string, tipo: TipoDeFesta): string {
  return `warroom.festaMarca.${marca}.${tipo}`
}

/**
 * Em que estágio a marca está AGORA: `null`, `'metade'` ou `'meta'`.
 *
 * `vendido: null` é "ninguém mediu" e nunca é estágio nenhum — a mesma regra do
 * resto do painel. Meta ausente ou <= 0 também não rende festa: com meta zero
 * qualquer venda seria 100 %, e a marca sem meta cadastrada comemoraria no
 * primeiro pedido.
 */
export function estagioDaMarca(vendido: number | null, meta: number): TipoDeFesta | null {
  if (typeof vendido !== 'number' || !Number.isFinite(vendido)) return null
  if (typeof meta !== 'number' || !Number.isFinite(meta) || meta <= 0) return null
  if (vendido >= meta) return 'meta'
  if (vendido >= meta / 2) return 'metade'
  return null
}

/**
 * As duas chaves que o estágio implica.
 *
 * Estar na meta implica ter passado da metade: as duas são marcadas juntas,
 * senão a festa da metade dispararia depois da da meta, fora de ordem. É a
 * mesma regra que o `usarComemoracao` aplica à meta geral.
 */
export function chavesDoEstagio(marca: string, estagio: TipoDeFesta | null): string[] {
  if (estagio === 'meta') return [chaveDaFesta(marca, 'metade'), chaveDaFesta(marca, 'meta')]
  if (estagio === 'metade') return [chaveDaFesta(marca, 'metade')]
  return []
}

/**
 * A ÂNCORA: tudo que as divisões já atingiram neste instante, como celebrado.
 *
 * Chamada uma vez, na primeira leitura da sessão, e o resultado é gravado. O que
 * já estava guardado tem precedência — recarregar a página não reabre festa
 * nenhuma.
 */
export function ancorar(divisoes: Divisao[]): string[] {
  const chaves: string[] = []
  for (const divisao of Array.isArray(divisoes) ? divisoes : []) {
    /* `divisao?.` e não `divisao.`: esta função é a última coisa entre o painel
       e um telão em branco. Item nulo no array — que o tipo diz que não existe,
       e que um erro de montagem do payload produziria — derrubaria a tela toda
       no instante em que o snapshot chega. */
    if (!divisao || typeof divisao.id !== 'string') continue
    chaves.push(...chavesDoEstagio(divisao.id, estagioDaMarca(divisao.vendido, divisao.meta)))
  }
  return chaves
}

/**
 * As festas NOVAS deste snapshot, já na ordem em que entram na fila.
 *
 * Ordem: da maior venda para a menor. Quando duas marcas cruzam no mesmo ciclo,
 * a sala vê primeiro a que vendeu mais — é a mesma ordem do pódio da
 * comemoração geral, e a única que não parece aleatória no telão.
 *
 * `celebradas` é o conjunto de chaves já comemoradas (memória + âncora + o que
 * esta mesma volta acabou de enfileirar). Esta função NÃO o modifica: quem
 * chama registra, e é isso que mantém a decisão testável sem `localStorage`.
 */
export function festasACelebrar(divisoes: Divisao[], celebradas: Set<string>): FestaDeMarca[] {
  const novas: FestaDeMarca[] = []

  for (const divisao of Array.isArray(divisoes) ? divisoes : []) {
    if (!divisao || typeof divisao.id !== 'string') continue
    const estagio = estagioDaMarca(divisao.vendido, divisao.meta)
    if (estagio === null) continue
    if (celebradas.has(chaveDaFesta(divisao.id, estagio))) continue
    /* O vendido é número aqui: `estagioDaMarca` já recusou `null` e `NaN`. O
       `?? 0` é só para o tipo, e nunca acontece. */
    novas.push({ marca: divisao.id, tipo: estagio, vendido: divisao.vendido ?? 0 })
  }

  return novas.sort((uma, outra) => outra.vendido - uma.vendido)
}

/**
 * A próxima festa a entrar na tela, ou `null` para não mexer em nada.
 *
 * Duas guardas, e são elas a regra de 14/10:
 *
 *  - **uma de cada vez**: com algo em cartaz, a fila espera o temporizador;
 *  - **a comemoração GERAL passa na frente**: enquanto ela está no ar, nenhuma
 *    festa de marca sobe. Duas telas cheias sobrepostas não é questão de
 *    empilhamento — é a sala vendo confete em cima de confete e não lendo
 *    nenhum dos dois.
 *
 * Função pura, e não um `if` dentro do gancho, para a regra ter teste: é a parte
 * que decide o que a sala vê quando duas coisas acontecem no mesmo ciclo.
 */
export function proximaDaFila(
  fila: FestaDeMarca[],
  emCartaz: FestaDeMarca | null,
  geralNoAr: boolean,
): FestaDeMarca | null {
  if (emCartaz !== null) return null
  if (geralNoAr) return null
  if (!Array.isArray(fila)) return null
  return fila[0] ?? null
}

/** Lê do armazém o que já foi celebrado. Falha em silêncio, como em `marcos.ts`. */
export function lerCelebradas(
  armazem: Pick<Storage, 'getItem'> | null,
  divisoes: Divisao[],
): Set<string> {
  const celebradas = new Set<string>()
  for (const divisao of Array.isArray(divisoes) ? divisoes : []) {
    if (!divisao || typeof divisao.id !== 'string') continue
    for (const tipo of ['metade', 'meta'] as TipoDeFesta[]) {
      const chave = chaveDaFesta(divisao.id, tipo)
      try {
        if (armazem?.getItem(chave) === '1') celebradas.add(chave)
      } catch {
        /* `localStorage` lança em janela com cookies bloqueados e no modo
           privado de alguns navegadores. Sem memória em disco a festa não
           repete dentro da sessão — o estado em memória basta para isso. */
      }
    }
  }
  return celebradas
}

/** Grava as chaves como celebradas. Falha em silêncio, pela mesma razão. */
export function gravarCelebradas(
  armazem: Pick<Storage, 'setItem'> | null,
  chaves: string[],
): void {
  for (const chave of Array.isArray(chaves) ? chaves : []) {
    try {
      armazem?.setItem(chave, '1')
    } catch {
      /* idem */
    }
  }
}

/** Quantos representantes a tela da festa mostra. Pedido: o top 15. */
export const REPRESENTANTES_NA_FESTA = 15

/** Quanto tempo esta festa fica na tela. */
export function duracaoDaFesta(tipo: TipoDeFesta): number {
  return tipo === 'meta' ? DURACAO_META_MARCA_MS : DURACAO_METADE_MARCA_MS
}

/** Quantas peças de confete esta festa joga. */
export function confetesDaFesta(tipo: TipoDeFesta): number {
  return tipo === 'meta' ? CONFETES_META_MARCA : CONFETES_METADE_MARCA
}

/**
 * O gatilho de TESTE pelo endereço: `?festa=lez:meta` ou `?festa=lez`.
 *
 * Existe porque a festa real acontece uma vez por marca no evento e ninguém
 * pode ensaiá-la: sem isto, a única forma de ver a tela no telão da sala é
 * esperar a marca bater a meta — e aí já é tarde para descobrir que o nome não
 * caberia. Sem `:tipo`, assume `meta`, que é a tela mais carregada (160 confetes
 * e as velas) e portanto a que vale conferir.
 *
 * Não dispensa a validação: ele não marca nada como celebrado e não mexe na
 * fila real, só mostra a tela.
 */
export function festaDeTeste(busca: string): { marca: string; tipo: TipoDeFesta } | null {
  let bruto: string | null = null
  try {
    bruto = new URLSearchParams(busca ?? '').get('festa')
  } catch {
    return null
  }
  if (bruto === null) return null
  const [marca, tipo] = bruto.split(':')
  const limpa = (marca ?? '').trim().toLowerCase()
  if (limpa === '') return null
  return { marca: limpa, tipo: tipo === 'metade' ? 'metade' : 'meta' }
}
